'use client'
import { FC, useState, useEffect } from 'react'
import Link from 'next/link'
import { Button, Banner, Checkbox, Input, PasswordInput } from '@/root/ui/tuuleliinid'
import { useAuth, useSignUp, useGetTranslations, useCustomRouter } from '@/root/shared-hooks'
import { validateEmail } from '@/root/shared-helpers'
import { useForm, FieldValues } from 'react-hook-form'
import { AuthVariant, PASSWORD_RULES } from '@/root/modules/tuuleliinid/LoginPage/constants'
import { getAuthErrorMessage } from '@/root/modules/tuuleliinid/LoginPage/helpers'

interface EmailFormProps {
  variant: AuthVariant
  onSignupSuccess?: () => void
}

export const EmailForm: FC<EmailFormProps> = ({ variant, onSignupSuccess }) => {
  const { getTranslation } = useGetTranslations()
  const { logIn, isLoading: logInLoading, error: logInError } = useAuth()
  const { signUpWithEmail, isLoading: signUpLoading, error: signUpError, reset: resetSignUp } = useSignUp()
  const { locale, push } = useCustomRouter()
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)

  const isSignup = variant === 'signup'
  const activeError = isSignup ? signUpError : logInError
  const isLoading = signUpLoading || logInLoading

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<FieldValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onBlur',
  })

  const emailValue = watch('email')
  const passwordValue = watch('password')

  useEffect(() => {
    reset({ email: '', password: '' })
    resetSignUp()
    setTermsAccepted(false)
  }, [variant, reset, resetSignUp])

  useEffect(() => {
    reset({ password: '' })
  }, [locale, reset])

  const onSubmit = async (data: FieldValues) => {
    if (isLoading) return

    if (isSignup) {
      const result = await signUpWithEmail({ email: data.email, password: data.password, locale })
      if (result.success && onSignupSuccess) {
        onSignupSuccess()
      }
    } else {
      await logIn({
        body: { email: data.email, password: data.password },
        rememberMe,
      })
    }
  }

  const isDisabled = [
    isLoading,
    errors.email,
    !emailValue,
    errors.password,
    !passwordValue,
    ...(isSignup ? [!termsAccepted] : []),
  ].some(Boolean)

  return (
    <>
      {activeError && <Banner variant="error" align="left" body={getAuthErrorMessage(activeError, getTranslation)} className="mb-4" />}

      <form className="flex w-full flex-col" onSubmit={handleSubmit(onSubmit)}>
        <Input
          type="email"
          label={getTranslation('authFieldEmailLabel')}
          placeholder={getTranslation('authFieldPlaceholder')}
          error={errors.email?.message as string}
          {...register('email', {
            required: getTranslation('requiredError'),
            validate: (value) => validateEmail(value) || getTranslation('invalidFormatText'),
          })}
        />

        <PasswordInput
          label={getTranslation('authFieldPasswordLabel')}
          placeholder={getTranslation('authFieldPlaceholder')}
          error={errors.password?.message as string}
          {...register('password', {
            required: getTranslation('requiredError'),
            ...(isSignup && {
              validate: (value) => PASSWORD_RULES.every(({ test }) => test(value)) || getTranslation('invalidPasswordText'),
            }),
          })}
        />

        {isSignup && (
          <div className="mt-4 flex items-start gap-2 text-sm">
            <Checkbox
              checked={termsAccepted}
              onChange={() => setTermsAccepted((prev) => !prev)}
              variant="success"
              label={getTranslation('authTermsAgreement')}
            />
            <Link
              href={`/${locale}/content/privacypolicy`}
              target="_blank"
              className="text-primary-base underline"
            >
              {getTranslation('authTermsLink')}
            </Link>
          </div>
        )}

        {!isSignup && (
          <Checkbox
            checked={rememberMe}
            onChange={() => setRememberMe((prev) => !prev)}
            variant="success"
            className="mt-4 text-sm"
            label={getTranslation('authRememberMe')}
          />
        )}

        <Button
          className="mt-6 flex h-12 w-full items-center justify-center whitespace-nowrap rounded-2xl text-lg font-bold disabled:hover:brightness-100"
          disabled={isDisabled}
          variant="contained"
          type="submit"
        >
          {isLoading
            ? getTranslation('authSending')
            : isSignup
              ? getTranslation('authCreateAccount')
              : getTranslation('signInEnter')}
        </Button>
      </form>

      {isSignup && (
        <p className="mt-4 w-full text-start text-sm">
          {getTranslation('authAlreadyHaveAccount')}{' '}
          <button type="button" onClick={() => push(`/${locale}/auth/login`)} className="text-primary-base cursor-pointer font-bold underline">
            {getTranslation('authLogInLink')}
          </button>
        </p>
      )}

      {!isSignup && (
        <Button
          type="button"
          onClick={() => push(`/${locale}/auth/recovery`)}
          variant="blue"
          className="mt-4 w-full text-start text-sm underline"
        >
          {getTranslation('signForgotPass')}
        </Button>
      )}
    </>
  )
}
